import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UnitFormatsProvider } from "@strava/units/react";

import { UNIT_METRIC } from '@strava/units';
import { Map, getElevationFromStream } from "@strava/feature-global-wasm-mre";

import I18n from '../../utils/I18n';
import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";

import { MapPhotos } from "./MapPhotos/MapPhotos";
import { MapCustomControls } from "./MapCustomControls/MapCustomControls";

import { getPhotosWithAttributes } from "./helpers/getPhotosWithAttributes";
import { getPrivacyZonesActivitySections } from "./helpers/getPrivacyZonesActivitySections";
import type { ActivityDetailsMapProps, LatLng, Photo } from "./types";

import styles from "./ActivityDetailsMap.scss";

const ActivityDetailsMap = ({
  activityId,
  mapBounds,
  streamData,
  defaultHighlight,
  interactive = true,
  showCreateRoute = true,
  showGpxDownload = true,
  showFullScreenToggle = false,
  measurementUnits = UNIT_METRIC,
  mapCreatedCallback
}: ActivityDetailsMapProps) => {

  const {
    latlng: locationStream = [],
    privacy: privacyStream = [],
    altitude: elevationStream = [],
    distance: distanceStream = []
  } = streamData || {};

  // TODO: Remove bebugging logs
  console.log("activityId:", activityId);
  console.log("mapBounds:", mapBounds);
  console.log("streamData:", streamData);
  console.log("locationStream:", locationStream);
  console.log("privacyStream:", privacyStream);
  console.log("elevationStream:", elevationStream);
  console.log("distanceStream:", distanceStream);
  console.log("defaultHighlight:", defaultHighlight);
  console.log("interactive:", interactive);
  console.log("showCreateRoute:", showCreateRoute);
  console.log("showGpxDownload:", showGpxDownload);
  console.log("showFullScreenToggle:", showFullScreenToggle);
  console.log("measurementUnits:", measurementUnits);
  console.log("mapCreatedCallback:", mapCreatedCallback);
  // Get the elevation data from the elevation and distance streams
  const elevation = useMemo(
    () =>
      elevationStream && elevationStream.length > 0 &&
      distanceStream && distanceStream?.length > 0
        ? getElevationFromStream(elevationStream, distanceStream)
        : [],
    [elevationStream, distanceStream]
  );

  // For some views, we want to highlight a section of the route by default, i.e. when viewing a map
  // for a segment within an activity
  const defaultHighlightLatLng = defaultHighlight
    ? [...locationStream.slice(defaultHighlight[0], defaultHighlight[1])]
    : null;

  const privacyZonesActivitySections = useMemo(() => 
    getPrivacyZonesActivitySections(privacyStream), [privacyStream]);

  const [currentMapBounds, setCurrentMapBounds] = useState(
    defaultHighlightLatLng ?? mapBounds
  );
  const [polylineHighlight, setPolylineHightlight] = useState(defaultHighlightLatLng ?? null);
  const [rabbit, setRabbit] = useState<LatLng | null>(null);
  const [mapPhotos, setMapPhotos] = useState<Photo[] | null>(null);
  
  const onHighlightPolyline = useCallback((e) => {
    const { start, end } = e.detail;
    const latLng = [...locationStream.slice(start, end)];
    setPolylineHightlight(latLng);
  }, [locationStream]);

  const onUnhighlightPolyline = useCallback(() => {
    setPolylineHightlight(null);
  }, []);

  const onHighlightPolylineAndCenterOnMap = useCallback((e) => {
    const { start, end } = e.detail;
    const latLng = [...locationStream.slice(start, end)];

    setPolylineHightlight(latLng);
    setCurrentMapBounds(latLng);
  }, [locationStream]);

  const onResetMapBounds = useCallback(() => {
    setCurrentMapBounds(mapBounds);
  }, [mapBounds]);

  const onSetRabbitPosition = useCallback((e) => {
    const { index } = e.detail;
    const latLng = locationStream[index];
    setRabbit(latLng);
  }, [locationStream]);

  const onHideRabbit = useCallback(() => {
    setRabbit(null);
  }, []);

  const onRenderMapImages = useCallback((e) => {
    const { photos = [] } = e.detail;

    // Get photos with required attributes (i.e. lat, lng, thumbnail) and set state
    setMapPhotos(getPhotosWithAttributes(photos));
  }, []);

  const eventListeners = useMemo(() => ({
    SetRabbitPosition: onSetRabbitPosition,
    HideRabbit: onHideRabbit,
    HighlightPolyline: onHighlightPolyline,
    UnhighlightPolyline: onUnhighlightPolyline,
    HighlightPolyLineAndCenterOnMap: onHighlightPolylineAndCenterOnMap,
    ResetMapBounds: onResetMapBounds,
    RenderMapImages: onRenderMapImages
  }), [
        onSetRabbitPosition,
        onHideRabbit,
        onHighlightPolyline,
        onUnhighlightPolyline,
        onHighlightPolylineAndCenterOnMap,
        onResetMapBounds,
        onRenderMapImages
      ]);

  // Attach and detach event listeners
  useEffect(() => {
    Object.entries(eventListeners).forEach(([event, listener]) => {
      document.addEventListener(event, listener);
    });

    /** 
     * Call the mapCreatedCallback if provided, AFTER event listeners are attached
     *
     * Currently, this is used to trigger a call to `renderPhotos()` in _photos.haml from the various activity type HAML partials
     * e.g. https://github.com/strava/active/blob/main/app/views/labs/activities/run.haml#L89-L91
     * 
     * `renderPhotos()` in turn dispatches a `RenderMapImages` event (https://github.com/strava/active/blob/main/app/views/labs/activities/_photos.haml#L24-L25)
     * This component i.e. ActivityDetailsMap listens for that event and updates state with the photos to be rendered on the map
     */
    if (mapCreatedCallback) {
      mapCreatedCallback()();
    }

    return () => {
      Object.entries(eventListeners).forEach(([event, listener]) => {
        document.removeEventListener(event, listener);
      });
    };
  }, [eventListeners, mapCreatedCallback]);

  return (
    <StravaPackageContextProviders> 
      <UnitFormatsProvider
        locale={I18n.language()}
        unitSystem={measurementUnits}
      >
        <Map
          polyline={currentMapBounds || locationStream}
          showPOIs={true}
          page={process.env.ACTIVITY_DETAILS_ANALYTICS_PAGE || ''}
          category={process.env.ACTIVITY_DETAILS_ANALYTICS_CATEGORY || ''}
          surfaceIdentifier={process.env.ACTIVITY_DETAILS_SURFACE_IDENTIFIER}
          mapboxToken={process.env.ACTIVITY_DETAILS_MAPBOX_TOKEN}
        >
          <Map.UIControls
            flyover={true}
            topRightOverride={
              <MapCustomControls
                {...(showCreateRoute && { createRouteURL: `/maps/create?activityId=${activityId}` })}
                {...(showGpxDownload && { gpxDownloadURL: `/activities/${activityId}/export_gpx` })}
                showMapTypeControl={true}
                showFullScreenToggle={showFullScreenToggle}
              />
            }
          />

          <Map.Activity
            id={activityId.toString()}
            polyline={locationStream}
            activitySections={privacyZonesActivitySections}
          />

          {/* If there are any privacy zones to display, show the legend */}
          {privacyZonesActivitySections.length > 0 && (
            <Map.PrivacyZonesLegend className={styles.privacyZonesLegend} />
          )}

          {rabbit && <Map.Scrubber latLng={rabbit} />}

          {elevation && <Map.Flyover polyline={locationStream} elevation={elevation} />}

          {polylineHighlight && (
            <Map.Segment
              id={`${activityId.toString()}-segment`}
              segmentPolyline={polylineHighlight}
            />
          )}

          {mapPhotos && <MapPhotos mapPhotos={mapPhotos} />}
        </Map>
      </UnitFormatsProvider>
    </StravaPackageContextProviders>
  );
};

export default ActivityDetailsMap;
